<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Membership Tiers</h1>
        <p class="gl-page-sub">Configure loyalty tiers, thresholds, and benefits</p>
      </div>
      <router-link to="/super-admin/loyalty/tiers/create" class="gl-btn-primary">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add Tier
      </router-link>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="gl-tier-grid">
      <div v-for="i in 4" :key="i" class="gl-tier-card">
        <div class="gl-tier-card-head" style="background:#f0f3f2">
          <div class="gl-skel" style="width:48px;height:48px;border-radius:50%;margin:0 auto 12px"></div>
          <div class="gl-skel" style="width:80px;height:14px;margin:0 auto 6px"></div>
          <div class="gl-skel" style="width:50px;height:10px;margin:0 auto"></div>
        </div>
        <div class="gl-tier-card-body">
          <div class="gl-skel" style="width:100%;height:10px;margin-bottom:8px"></div>
          <div class="gl-skel" style="width:70%;height:10px;margin-bottom:16px"></div>
          <div v-for="j in 3" :key="j" class="gl-skel" style="width:90%;height:9px;margin-bottom:7px"></div>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="!tiers.length" class="gl-card">
      <div class="gl-empty">
        <div class="gl-empty-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        </div>
        <div class="gl-empty-title">No tiers configured</div>
        <div class="gl-empty-sub">Add your first membership tier to get started</div>
        <router-link to="/super-admin/loyalty/tiers/create" class="gl-btn-primary" style="margin-top:16px">Add First Tier</router-link>
      </div>
    </div>

    <!-- Tier Cards Grid -->
    <div v-else class="gl-tier-grid">
      <div v-for="tier in tiers" :key="tier.id" class="gl-tier-card">
        <!-- Coloured Header -->
        <div class="gl-tier-card-head" :style="{ background: tierGradient(tier) }">
          <div class="gl-tier-order-badge">#{{ tier.sort_order }}</div>
          <div class="gl-tier-icon">{{ tierEmoji(tier) }}</div>
          <div class="gl-tier-card-name">{{ tier.name }}</div>
          <div class="gl-tier-card-slug">{{ tier.slug }}</div>
          <span v-if="tier.is_default" class="gl-tier-default-badge">Default</span>
        </div>

        <!-- Stats -->
        <div class="gl-tier-card-body">
          <div class="gl-tier-stats">
            <div class="gl-tier-stat">
              <div class="gl-tier-stat-val">{{ tier.min_points.toLocaleString() }}</div>
              <div class="gl-tier-stat-label">Min Points</div>
            </div>
            <div class="gl-tier-stat-divider"></div>
            <div class="gl-tier-stat">
              <div class="gl-tier-stat-val">{{ Number(tier.points_multiplier).toFixed(2) }}×</div>
              <div class="gl-tier-stat-label">Multiplier</div>
            </div>
            <div class="gl-tier-stat-divider"></div>
            <div class="gl-tier-stat">
              <div class="gl-tier-stat-val">{{ tier.users_count ?? 0 }}</div>
              <div class="gl-tier-stat-label">Customers</div>
            </div>
          </div>

          <!-- Benefits -->
          <div class="gl-tier-benefits">
            <div v-if="!benefitsOf(tier).length" class="gl-tier-no-benefits">No benefits defined</div>
            <div v-for="(b, i) in benefitsOf(tier)" :key="i" class="gl-tier-benefit-row">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
              <span>{{ b }}</span>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="gl-tier-card-footer">
          <router-link :to="`/super-admin/loyalty/tiers/${tier.id}/edit`" class="gl-tier-action-btn gl-tier-action-edit">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Edit
          </router-link>
          <button
            class="gl-tier-action-btn gl-tier-action-delete"
            :disabled="tier.is_default"
            :title="tier.is_default ? 'Cannot delete the default tier' : 'Delete tier'"
            @click="confirmDelete(tier)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
            Delete
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <div v-if="showDeleteModal" class="gl-modal-overlay" @click.self="showDeleteModal = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <div>
            <div class="gl-modal-title">Delete Tier</div>
            <div v-if="selected" class="gl-modal-sub">{{ selected.name }}</div>
          </div>
          <button class="gl-modal-close" @click="showDeleteModal = false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <p style="margin:0;color:#374151">
            Delete tier <strong>{{ selected?.name }}</strong>? This cannot be undone and will remove all tier assignments from customers.
          </p>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-ghost" @click="showDeleteModal = false">Cancel</button>
          <button class="gl-btn-danger" @click="doDelete" :disabled="deleting">
            <span v-if="deleting" class="gl-spin"></span>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminTierApi } from '@/services/superAdminApi';

const TIER_THEMES = [
  { gradient: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' },
  { gradient: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' },
  { gradient: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' },
  { gradient: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' },
  { gradient: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' },
];
const TIER_EMOJIS = { bronze: '🥉', silver: '🥈', gold: '🥇', platinum: '💎', diamond: '💠', free: '⭐', starter: '🌱', premium: '👑', vip: '🏆' };

export default {
  name: 'SATierList',
  data() {
    return { loading: true, deleting: false, tiers: [], selected: null, showDeleteModal: false };
  },
  mounted() { this.fetchTiers(); },
  methods: {
    async fetchTiers() {
      try {
        this.loading = true;
        const r = await superAdminTierApi.list();
        this.tiers = r.data.data;
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    benefitsOf(tier) {
      if (!tier.benefits) return [];
      if (Array.isArray(tier.benefits)) return tier.benefits;
      if (typeof tier.benefits === 'string') {
        try { const p = JSON.parse(tier.benefits); return Array.isArray(p) ? p : []; } catch { return []; }
      }
      return [];
    },
    tierGradient(tier) {
      const slug = (tier.slug || '').toLowerCase();
      for (const [key, emoji] of Object.entries(TIER_EMOJIS)) {
        if (slug.includes(key)) {
          const map = { bronze: TIER_THEMES[2], silver: TIER_THEMES[1], gold: TIER_THEMES[2], platinum: TIER_THEMES[3], diamond: TIER_THEMES[3], free: TIER_THEMES[0], starter: TIER_THEMES[0], premium: TIER_THEMES[4], vip: TIER_THEMES[4] };
          if (map[key]) return map[key].gradient;
        }
      }
      return TIER_THEMES[(tier.sort_order ?? 0) % TIER_THEMES.length].gradient;
    },
    tierEmoji(tier) {
      const slug = (tier.slug || '').toLowerCase();
      for (const [key, emoji] of Object.entries(TIER_EMOJIS)) {
        if (slug.includes(key)) return emoji;
      }
      return '⭐';
    },
    confirmDelete(tier) { this.selected = tier; this.showDeleteModal = true; },
    async doDelete() {
      try {
        this.deleting = true;
        await superAdminTierApi.delete(this.selected.id);
        this.showDeleteModal = false;
        this.fetchTiers();
      } catch (e) {
        alert(e.response?.data?.message || 'Failed to delete tier');
      } finally { this.deleting = false; this.selected = null; }
    }
  }
};
</script>

<style>
/* ── Page ── */
.gl-page { padding: 24px; max-width: 1400px; }
.gl-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 28px; gap: 16px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -.3px; }
.gl-page-sub { font-size: 13px; color: #6b7f7b; margin: 0; }

/* ── Buttons ── */
.gl-btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #900C27; color: #fff; border: none; border-radius: 10px; padding: 9px 16px; font-size: 13.5px; font-weight: 600; cursor: pointer; text-decoration: none; transition: background .15s; }
.gl-btn-primary:hover { background: #1a5048; color: #fff; }
.gl-btn-ghost { background: transparent; border: 1.5px solid #e0e7e5; color: #374151; border-radius: 9px; padding: 9px 18px; font-size: 13.5px; font-weight: 500; cursor: pointer; }
.gl-btn-danger { background: #dc2626; color: #fff; border: none; border-radius: 9px; padding: 9px 18px; font-size: 13.5px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }

/* ── Card ── */
.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; }

/* ── Tier grid ── */
.gl-tier-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }

/* ── Tier card ── */
.gl-tier-card { background: #fff; border: 1px solid #eaedec; border-radius: 18px; overflow: hidden; display: flex; flex-direction: column; transition: box-shadow .2s, transform .2s; }
.gl-tier-card:hover { box-shadow: 0 8px 32px rgba(0,0,0,.1); transform: translateY(-2px); }

/* Card header */
.gl-tier-card-head { position: relative; padding: 28px 20px 22px; text-align: center; color: #fff; }
.gl-tier-order-badge { position: absolute; top: 12px; left: 14px; background: rgba(255,255,255,.25); border-radius: 20px; padding: 2px 9px; font-size: 11px; font-weight: 700; letter-spacing: .3px; }
.gl-tier-icon { font-size: 36px; margin-bottom: 8px; line-height: 1; }
.gl-tier-card-name { font-size: 17px; font-weight: 800; letter-spacing: -.2px; margin-bottom: 2px; }
.gl-tier-card-slug { font-size: 11.5px; opacity: .75; font-family: 'SF Mono', monospace; }
.gl-tier-default-badge { position: absolute; top: 12px; right: 12px; background: rgba(255,255,255,.3); border: 1px solid rgba(255,255,255,.5); border-radius: 20px; padding: 2px 9px; font-size: 10.5px; font-weight: 700; color: #fff; }

/* Card body */
.gl-tier-card-body { flex: 1; padding: 20px; }

/* Stats row */
.gl-tier-stats { display: flex; align-items: center; background: #f8faf9; border-radius: 12px; padding: 14px 8px; margin-bottom: 18px; }
.gl-tier-stat { flex: 1; text-align: center; }
.gl-tier-stat-val { font-size: 16px; font-weight: 800; color: #111827; line-height: 1; margin-bottom: 3px; }
.gl-tier-stat-label { font-size: 10px; color: #9ca3af; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; }
.gl-tier-stat-divider { width: 1px; height: 32px; background: #e5e7eb; flex-shrink: 0; }

/* Benefits */
.gl-tier-benefits { display: flex; flex-direction: column; gap: 7px; }
.gl-tier-benefit-row { display: flex; align-items: flex-start; gap: 7px; font-size: 12.5px; color: #374151; line-height: 1.4; }
.gl-tier-benefit-row svg { color: #900C27; margin-top: 1px; }
.gl-tier-no-benefits { font-size: 12px; color: #9ca3af; font-style: italic; }

/* Card footer */
.gl-tier-card-footer { display: flex; gap: 8px; padding: 14px 18px; border-top: 1px solid #f0f3f2; }
.gl-tier-action-btn { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 8px 12px; border-radius: 9px; font-size: 13px; font-weight: 600; cursor: pointer; text-decoration: none; transition: all .15s; border: 1.5px solid; }
.gl-tier-action-edit { border-color: #900C27; color: #900C27; background: #fff; }
.gl-tier-action-edit:hover { background: #e8f4f1; color: #900C27; }
.gl-tier-action-delete { border-color: #e0e7e5; color: #6b7f7b; background: #fff; }
.gl-tier-action-delete:hover:not(:disabled) { border-color: #dc2626; color: #dc2626; background: #fef2f2; }
.gl-tier-action-delete:disabled { opacity: .35; cursor: not-allowed; }

/* ── Empty ── */
.gl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 24px; }
.gl-empty-icon { width: 64px; height: 64px; border-radius: 16px; background: #f0f3f2; display: flex; align-items: center; justify-content: center; color: #9ca3af; margin-bottom: 16px; }
.gl-empty-title { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 4px; }
.gl-empty-sub { font-size: 13px; color: #9ca3af; }

/* ── Skeleton ── */
.gl-skel { background: linear-gradient(90deg, #f0f3f2 25%, #e8edeb 50%, #f0f3f2 75%); background-size: 200% 100%; border-radius: 6px; animation: gl-shimmer 1.5s infinite; display: block; }
@keyframes gl-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.gl-skel-badge { width: 60px; height: 22px; border-radius: 20px; background: #f0f3f2; animation: gl-shimmer 1.5s infinite; }
.gl-skel-btn { width: 32px; height: 32px; border-radius: 8px; background: #f0f3f2; animation: gl-shimmer 1.5s infinite; }

/* ── Spinner ── */
.gl-spin { display: inline-block; width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: gl-spin .6s linear infinite; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* ── Modal ── */
.gl-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1050; padding: 16px; }
.gl-modal { background: #fff; border-radius: 18px; width: 100%; max-width: 440px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.18); position: relative; }
.gl-modal-header { padding: 22px 56px 16px 24px; border-bottom: 1px solid #f0f3f2; }
.gl-modal-title { font-size: 16px; font-weight: 700; color: #111827; }
.gl-modal-sub { font-size: 12.5px; color: #6b7f7b; margin-top: 2px; }
.gl-modal-close { position: absolute; top: 16px; right: 16px; width: 32px; height: 32px; border-radius: 8px; border: 1px solid #eaedec; background: #fff; color: #6b7f7b; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.gl-modal-close:hover { background: #f8faf9; }
.gl-modal-body { padding: 20px 24px; }
.gl-modal-footer { padding: 16px 24px; border-top: 1px solid #f0f3f2; display: flex; justify-content: flex-end; gap: 10px; }

/* ── Responsive ── */
@media (max-width: 1100px) { .gl-tier-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 767px) {
  .gl-page { padding: 16px; }
  .gl-page-title { font-size: 18px; }
  .gl-tier-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .gl-modal { border-radius: 20px 20px 0 0; max-width: 100%; margin: auto 0 0; }
  .gl-modal-overlay { align-items: flex-end; padding: 0; }
}
@media (max-width: 480px) { .gl-tier-grid { grid-template-columns: 1fr; } }

/* ── Dark mode ── */
body.dark-only .gl-page-title { color: #f1f5f9; }
body.dark-only .gl-page-sub { color: #94a3b8; }
body.dark-only .gl-tier-card { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-tier-stat-val { color: #f1f5f9; }
body.dark-only .gl-tier-stats { background: #162220; }
body.dark-only .gl-tier-stat-divider { background: #2d3f3b; }
body.dark-only .gl-tier-benefit-row { color: #e2e8f0; }
body.dark-only .gl-tier-card-footer { border-color: #2d3f3b; }
body.dark-only .gl-tier-action-edit { background: #1e2a27; }
body.dark-only .gl-tier-action-delete { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-card { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-empty-title { color: #e2e8f0; }
body.dark-only .gl-empty-icon { background: #162220; }
body.dark-only .gl-modal { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-modal-title { color: #f1f5f9; }
body.dark-only .gl-modal-header,
body.dark-only .gl-modal-footer { border-color: #2d3f3b; }
body.dark-only .gl-modal-close { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-modal-body p { color: #e2e8f0; }
body.dark-only .gl-btn-ghost { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
</style>
